import { useState, useRef, useEffect } from "react";

import {
  Box,
  Chip,
  Grid,
  Typography,
  TextField,
  IconButton,
  CircularProgress,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Button,
  Paper,
  Collapse,
  Popover,
  Tooltip,
  useTheme
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import ArchiveIcon from "@mui/icons-material/Archive";
import UnarchiveIcon from "@mui/icons-material/Unarchive";
import HistoryIcon from "@mui/icons-material/History";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ThumbUpOutlinedIcon from "@mui/icons-material/ThumbUpOutlined";
import ThumbDownOutlinedIcon from "@mui/icons-material/ThumbDownOutlined";
import ThumbUpIcon from "@mui/icons-material/ThumbUp";
import ThumbDownIcon from "@mui/icons-material/ThumbDown";
import { useHistory } from "react-router-dom";
import Markdown from "react-markdown";
import remarkGfm from "remark-gfm";

import ScoutMark from "../atoms/icons/ScoutMark";

import { fetchOurApi } from "../../helpers/utils/apiCalls";
import { getAccountId } from "../../helpers/accountHelpers";
import { generateTempId } from "../../helpers/otherHelpers";
import {
  useCachedDisplayData,
  useCachedFirebaseCrud
} from "../../helpers/firebase";
import usePlatformLayoutContext from "../../helpers/contexts/platformLayout";

const MarkdownLink = ({ href, children }) => {
  const history = useHistory();
  const isInternal = href?.startsWith("/");

  const handleClick = (e) => {
    e.preventDefault();
    history.push(href);
  };

  if (isInternal) {
    return (
      <a
        href={href}
        onClick={handleClick}
        style={{
          color: "inherit",
          textDecoration: "underline",
          cursor: "pointer"
        }}
      >
        {children}
      </a>
    );
  }

  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      style={{ color: "inherit", textDecoration: "underline" }}
    >
      {children}
    </a>
  );
};

const markdownComponents = {
  p: ({ children }) => (
    <Typography
      variant="body2"
      color="inherit"
      paragraph
      style={{ margin: "0 0 8px" }}
    >
      {children}
    </Typography>
  ),
  h1: ({ children }) => (
    <Typography
      variant="subtitle1"
      color="inherit"
      style={{ margin: "8px 0 4px" }}
    >
      {children}
    </Typography>
  ),
  h2: ({ children }) => (
    <Typography
      variant="subtitle1"
      color="inherit"
      style={{ margin: "8px 0 4px" }}
    >
      {children}
    </Typography>
  ),
  h3: ({ children }) => (
    <Typography
      variant="subtitle2"
      color="inherit"
      style={{ margin: "6px 0 4px" }}
    >
      {children}
    </Typography>
  ),
  strong: ({ children }) => <strong>{children}</strong>,
  ul: ({ children }) => (
    <ul style={{ margin: "4px 0", paddingLeft: 20 }}>{children}</ul>
  ),
  ol: ({ children }) => (
    <ol style={{ margin: "4px 0", paddingLeft: 20 }}>{children}</ol>
  ),
  li: ({ children }) => (
    <li>
      <Typography variant="body2" color="inherit" component="span">
        {children}
      </Typography>
    </li>
  ),
  a: MarkdownLink
};

const UserMessage = ({ content }) => {
  const { palette } = useTheme();
  return (
    <Box display="flex" justifyContent="flex-end">
      <Box
        px={2}
        py={1}
        style={{
          backgroundColor: palette.secondary.main,
          color: palette.common.white,
          borderRadius: "8px",
          maxWidth: "75%"
        }}
      >
        <Typography variant="body2" color="inherit">
          {content}
        </Typography>
      </Box>
    </Box>
  );
};

const NegativeFeedbackForm = ({ onSubmit, onCancel }) => {
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    setSubmitting(true);
    await onSubmit(comment);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  return (
    <Box display="flex" flexDirection="column" style={{ gap: 4 }}>
      <TextField
        autoFocus
        fullWidth
        size="small"
        variant="outlined"
        placeholder="Tell us what was wrong (optional)"
        value={comment}
        onChange={(e) => setComment(e.target.value)}
        onKeyDown={handleKeyDown}
        multiline
        maxRows={3}
        disabled={submitting}
      />
      <Box display="flex" justifyContent="flex-end" style={{ gap: 4 }}>
        <Button size="small" onClick={onCancel} disabled={submitting}>
          Cancel
        </Button>
        <Button
          size="small"
          variant="contained"
          color="primary"
          onClick={handleSubmit}
          disabled={submitting}
        >
          {submitting ? "Sending..." : "Submit"}
        </Button>
      </Box>
    </Box>
  );
};

const AssistantMessage = ({ content, onRate, existingRating }) => {
  const { palette } = useTheme();
  const [showFeedbackForm, setShowFeedbackForm] = useState(false);

  const handleNegativeClick = () => setShowFeedbackForm(true);

  const handleSubmitNegative = async (comment) => {
    await onRate("negative", comment);
    setShowFeedbackForm(false);
  };

  return (
    <Box display="flex" justifyContent="flex-start">
      <Box
        display="flex"
        flexDirection="column"
        style={{ maxWidth: "75%", gap: 4 }}
      >
        <Box
          px={2}
          py={1}
          style={{
            backgroundColor: palette.backgroundGray.dark,
            color: palette.text.primary,
            borderRadius: "8px"
          }}
        >
          <Markdown components={markdownComponents} remarkPlugins={[remarkGfm]}>
            {content}
          </Markdown>
        </Box>
        {!showFeedbackForm && (
          <Box display="flex">
            <Tooltip title="Good response">
              <span>
                <IconButton
                  size="small"
                  disabled={!!existingRating}
                  onClick={() => onRate("positive")}
                >
                  {existingRating === "positive" ? (
                    <ThumbUpIcon fontSize="small" color="primary" />
                  ) : (
                    <ThumbUpOutlinedIcon fontSize="small" />
                  )}
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Bad response">
              <span>
                <IconButton
                  size="small"
                  disabled={!!existingRating}
                  onClick={handleNegativeClick}
                >
                  {existingRating === "negative" ? (
                    <ThumbDownIcon fontSize="small" color="error" />
                  ) : (
                    <ThumbDownOutlinedIcon fontSize="small" />
                  )}
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        )}
        {showFeedbackForm && (
          <NegativeFeedbackForm
            onSubmit={handleSubmitNegative}
            onCancel={() => setShowFeedbackForm(false)}
          />
        )}
      </Box>
    </Box>
  );
};

const ThinkingIndicator = () => {
  const { palette } = useTheme();
  return (
    <Box display="flex" justifyContent="flex-start">
      <Box
        px={2}
        py={1}
        style={{
          backgroundColor: palette.backgroundGray.main,
          borderRadius: "8px"
        }}
      >
        <Grid container spacing={1} alignItems="center">
          <Grid item>
            <CircularProgress size={14} />
          </Grid>
          <Grid item>
            <Typography variant="body2" color="textSecondary">
              Thinking...
            </Typography>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

const sortByUpdated = (arr) =>
  [...arr].sort((a, b) => (b.updatedAt || 0) - (a.updatedAt || 0));

const buildConversationPreview = (conv) => {
  if (conv?.context === "emissions-survey") {
    return conv.surveyYear
      ? `Emissions Survey — ${conv.surveyYear}`
      : "Emissions Survey";
  }
  const firstUserTurn = (conv?.turns || []).find((t) => t.role === "user");
  return firstUserTurn?.content?.slice(0, 80) || "New conversation";
};

const ConvCard = ({
  conv,
  isSelected,
  onSelect,
  onAction,
  actionIcon,
  actionLabel
}) => {
  const preview = buildConversationPreview(conv);
  const date = conv.updatedAt
    ? new Date(conv.updatedAt).toLocaleDateString()
    : "";

  return (
    <ListItem disablePadding>
      <Paper
        elevation={isSelected ? 4 : 2}
        style={{ width: "100%", borderRadius: "8px" }}
      >
        <Box display="flex" alignItems="center">
          <ListItemButton
            selected={isSelected}
            onClick={() => onSelect(conv.id)}
            style={{ borderRadius: "8px", flexGrow: 1 }}
          >
            <ListItemText primary={preview} secondary={date} />
            <Tooltip title={actionLabel}>
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  return onAction(conv.id);
                }}
              >
                {actionIcon}
              </IconButton>
            </Tooltip>
          </ListItemButton>
        </Box>
      </Paper>
    </ListItem>
  );
};

const ConversationHistoryPanel = ({
  conversations,
  conversationsLoading,
  selectedId,
  onSelect,
  onNew
}) => {
  const [showArchived, setShowArchived] = useState(false);
  const { updateCollectionDoc } = useCachedFirebaseCrud();

  const active = (conversations || []).filter((c) => !c.archived);
  const archived = (conversations || []).filter((c) => c.archived);

  const handleArchive = (id) =>
    updateCollectionDoc("agent-conversations", id, { archived: true });
  const handleUnarchive = (id) =>
    updateCollectionDoc("agent-conversations", id, { archived: false });

  return (
    <Box display="flex" flexDirection="column" style={{ gap: 16 }}>
      {!!active.length && (
        <Button
          fullWidth
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={onNew}
        >
          New Chat
        </Button>
      )}
      {conversationsLoading ? (
        <Box display="flex" justifyContent="center">
          <CircularProgress size={20} />
        </Box>
      ) : active.length === 0 ? (
        <Typography variant="body2" color="textSecondary">
          No previous conversations yet.
        </Typography>
      ) : (
        <List
          dense
          disablePadding
          style={{ display: "flex", flexDirection: "column", gap: 8 }}
        >
          {sortByUpdated(active).map((conv) => (
            <ConvCard
              key={conv.id}
              conv={conv}
              isSelected={conv.id === selectedId}
              onSelect={onSelect}
              onAction={handleArchive}
              actionIcon={<ArchiveIcon fontSize="small" />}
              actionLabel="Archive"
            />
          ))}
        </List>
      )}
      {archived.length > 0 && (
        <Box display="flex" flexDirection="column" style={{ gap: 8 }}>
          <Button
            size="small"
            color="primary"
            onClick={() => setShowArchived((v) => !v)}
          >
            {showArchived
              ? "Hide archived"
              : `Show archived (${archived.length})`}
          </Button>
          <Collapse in={showArchived}>
            <List
              dense
              disablePadding
              style={{ display: "flex", flexDirection: "column", gap: 8 }}
            >
              {sortByUpdated(archived).map((conv) => (
                <ConvCard
                  key={conv.id}
                  conv={conv}
                  isSelected={conv.id === selectedId}
                  onSelect={onSelect}
                  onAction={handleUnarchive}
                  actionIcon={<UnarchiveIcon fontSize="small" />}
                  actionLabel="Unarchive"
                />
              ))}
            </List>
          </Collapse>
        </Box>
      )}
    </Box>
  );
};

const ScoutBar = () => {
  const { palette } = useTheme();
  const { activateSnackbar } = usePlatformLayoutContext();

  const [conversations, conversationsLoading] = useCachedDisplayData(
    "agent-conversations"
  );
  const [selectedConversationId, setSelectedConversationId] = useState(null);
  const [newConversationId] = useState(() => String(generateTempId()));
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [ratings, setRatings] = useState({});
  const [isOpen, setIsOpen] = useState(false);
  const [historyAnchorEl, setHistoryAnchorEl] = useState(null);
  const [conversationHeight, setConversationHeight] = useState(300);

  const bottomRef = useRef(null);
  const replyInputRef = useRef(null);
  const dragRef = useRef({ isDragging: false, startY: 0, startHeight: 0 });

  const conversationId = selectedConversationId || newConversationId;

  useEffect(() => {
    if (!selectedConversationId) {
      return;
    }
    const conv = (conversations || []).find(
      (c) => c.id === selectedConversationId
    );
    setMessages(
      conv
        ? (conv.turns || []).map(({ role, content, sources }) => ({
            role,
            content,
            sources: sources || []
          }))
        : []
    );
  }, [selectedConversationId, conversations]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, isLoading]);

  useEffect(() => {
    if (!isLoading && isOpen && messages.length > 0) {
      replyInputRef.current?.focus();
    }
  }, [isLoading, isOpen, messages.length]);

  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!dragRef.current.isDragging) return;
      const delta = e.clientY - dragRef.current.startY;
      const newHeight = Math.min(
        Math.max(dragRef.current.startHeight + delta, 100),
        Math.round(window.innerHeight * 0.8)
      );
      setConversationHeight(newHeight);
    };
    const handleMouseUp = () => {
      dragRef.current = { ...dragRef.current, isDragging: false };
    };
    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, []);

  const sendMessage = async (messageOverride) => {
    const trimmed = (messageOverride || input).trim();
    if (!trimmed || isLoading) return;

    const companyId = getAccountId();
    setMessages((prev) => [...prev, { role: "user", content: trimmed }]);
    setInput("");
    setIsOpen(true);
    setIsLoading(true);

    try {
      await fetchOurApi({
        path: "/agents/chat",
        method: "POST",
        data: { companyId, conversationId, message: trimmed },
        callback: ({ response, sources }) =>
          setMessages((prev) => [
            ...prev,
            {
              role: "assistant",
              content: response || "No response received.",
              sources: sources || []
            }
          ])
      });
    } catch {
      setMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          content:
            "Something went wrong processing your request. Please try again.",
          sources: []
        }
      ]);
    }

    setIsLoading(false);
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const handleRate = async (messageIdx, rating, comment = "") => {
    setRatings((prev) => ({ ...prev, [messageIdx]: rating }));
    activateSnackbar({
      message:
        rating === "positive"
          ? "Thanks for the feedback!"
          : "Thanks! We'll review this response.",
      alert: rating === "positive" ? "success" : "info"
    });

    const companyId = getAccountId();
    const assistantTurn = messages[messageIdx];
    const assistantMessage = assistantTurn?.content || "";
    const userMessage = messages[messageIdx - 1]?.content || "";

    await fetchOurApi({
      path: "/agents/feedback",
      method: "POST",
      data: {
        companyId,
        userMessage,
        assistantMessage,
        rating,
        body: comment,
        sources: assistantTurn?.sources || []
      }
    });
  };

  const handleDragStart = (e) => {
    e.preventDefault();
    dragRef.current = {
      isDragging: true,
      startY: e.clientY,
      startHeight: conversationHeight
    };
  };

  const handleNewConversation = () => {
    setSelectedConversationId(null);
    setMessages([]);
    setInput("");
    setIsOpen(false);
    setHistoryAnchorEl(null);
  };

  const handleSelectConversation = (id) => {
    setSelectedConversationId(id);
    setIsOpen(true);
    setHistoryAnchorEl(null);
  };

  return (
    <Box p={2} style={{ background: palette.backgroundGray.main }}>
      <Grid container wrap="nowrap" alignItems="flex-start" spacing={2}>
        <Grid item>
          <Tooltip title="Scout is your AI sustainability guide. Ask anything about your carbon footprint, how to use the platform, certifications, reporting, or how to reduce your emissions.">
            <Box component="span">
              <ScoutMark size={32} />
            </Box>
          </Tooltip>
        </Grid>
        <Grid item xs>
          <Collapse in={messages.length === 0}>
            <Box display="flex" flexDirection="column" style={{ gap: 8 }}>
              <TextField
                fullWidth
                variant="standard"
                size="small"
                placeholder="Hi! I'm Scout. Ask me anything"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={onKeyDown}
                multiline
                maxRows={4}
                disabled={isLoading}
              />
              <Collapse in={!input}>
                <Box display="flex" flexWrap="wrap" style={{ gap: 8 }}>
                  <Chip
                    label="What are my biggest sources of emissions?"
                    variant="outlined"
                    size="small"
                    onClick={() =>
                      sendMessage("What are my biggest sources of emissions?")
                    }
                  />
                  <Chip
                    label="Help me create a plan to reduce my emissions"
                    variant="outlined"
                    size="small"
                    onClick={() =>
                      sendMessage(
                        "Help me create a plan to reduce my emissions"
                      )
                    }
                  />
                </Box>
              </Collapse>
            </Box>
          </Collapse>
          <Collapse in={!isOpen && messages.length > 0}>
            <Box display="flex" justifyContent="flex-end">
              <Box
                px={2}
                py={1}
                style={{
                  backgroundColor: palette.secondary.main,
                  color: palette.common.white,
                  borderRadius: "8px",
                  maxWidth: "75%"
                }}
              >
                <Typography variant="body2" color="inherit" noWrap>
                  {messages.find((m) => m.role === "user")?.content}
                </Typography>
              </Box>
            </Box>
          </Collapse>
          <Collapse in={isOpen && messages.length > 0}>
            <Box display="flex" flexDirection="column" style={{ gap: 12 }}>
              <Box
                style={{
                  gap: 12,
                  maxHeight: conversationHeight,
                  overflowY: "auto"
                }}
                display="flex"
                flexDirection="column"
              >
                {messages.map((msg, idx) => (
                  <Box key={`msg-${idx}`}>
                    {msg.role === "user" ? (
                      <UserMessage content={msg.content} />
                    ) : (
                      <AssistantMessage
                        content={msg.content}
                        onRate={(rating) => handleRate(idx, rating)}
                        existingRating={ratings[idx]}
                      />
                    )}
                  </Box>
                ))}
                {isLoading && (
                  <Box>
                    <ThinkingIndicator />
                  </Box>
                )}
                <div ref={bottomRef} />
              </Box>
              <TextField
                fullWidth
                variant="standard"
                size="small"
                placeholder="Reply..."
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={onKeyDown}
                multiline
                maxRows={4}
                disabled={isLoading}
                inputRef={replyInputRef}
              />
            </Box>
          </Collapse>
        </Grid>
        <Grid item>
          <Tooltip title="Conversation history">
            <IconButton
              size="small"
              onClick={(e) => setHistoryAnchorEl(e.currentTarget)}
            >
              <HistoryIcon />
            </IconButton>
          </Tooltip>
          <Popover
            open={Boolean(historyAnchorEl)}
            anchorEl={historyAnchorEl}
            onClose={() => setHistoryAnchorEl(null)}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
          >
            <Box
              p={2}
              style={{ width: "320px", maxHeight: "400px", overflowY: "auto" }}
            >
              <ConversationHistoryPanel
                conversations={conversations || []}
                conversationsLoading={conversationsLoading}
                selectedId={selectedConversationId}
                onSelect={handleSelectConversation}
                onNew={handleNewConversation}
              />
            </Box>
          </Popover>
        </Grid>
        <Grid item>
          <Tooltip title={isOpen ? "Collapse" : "Expand"}>
            <span>
              <IconButton
                size="small"
                onClick={() => setIsOpen((v) => !v)}
                disabled={messages.length === 0}
              >
                {isOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </IconButton>
            </span>
          </Tooltip>
        </Grid>
      </Grid>
      {isOpen && messages.length > 0 && (
        <Box
          onMouseDown={handleDragStart}
          display="flex"
          justifyContent="center"
          alignItems="center"
          style={{ height: 12, cursor: "ns-resize", marginTop: 4 }}
        >
          <Box
            style={{
              width: 40,
              height: 3,
              borderRadius: 2,
              backgroundColor: palette.backgroundGray.dark
            }}
          />
        </Box>
      )}
    </Box>
  );
};
export default ScoutBar;
